import React, { useState } from 'react';
import { defineMessages } from 'react-intl';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { tokens } from '@companycam/slab';
import { ErrorMessage } from 'formik';

import ReportPageLayouts from 'images/ReportPageLayouts.png';
import { LAYOUT_IMAGES } from './constants';
import { TextInput } from 'components/core/forms';
import SelectCircle from 'components/shared/DesignSystem//SelectCircle';
import FormMessage from 'components/shared/DesignSystem/FormMessage';
import { Button } from '@companycam/slab-web';

const FormRow = styled.div`
  margin-top: 0.25rem;
`;

const FormLabel = styled.div`
  font-weight: bold;
  font-size: 14px;
`;

const LayoutList = styled.ul`
  display: grid;
  grid-template-rows: repeat(3, minmax(0, 1fr));
  row-gap: 10px;
  margin-top: 0px;
  border-radius: var(--cc_size_border_radius_l);

  li {
    list-style: none;
    &.selected {
      color: var(--cc_color_link_primary);
    }
  }
`;

const LayoutContainer = styled.div`
  border: 1px solid var(--cc_color_border_default);
  background-color: var(--cc_color_background_1);
  padding: 20px;
  border-radius: var(--cc_size_border_radius_l);

  &:hover {
    border-color: var(--cc_color_background_3);
  }

  &.selected {
    border-color: var(--cc_color_link_primary);
  }

  @media (min-width: 640px) {
    display: flex;
    justify-content: space-between;
  }
`;

const OptionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 0.875rem;
  line-height: 1.25rem;
`;

const messages = defineMessages({
  title: {
    id: 'Reports.createForm.title',
    defaultMessage: 'Report Title',
    description: 'Create Reports form title field',
  },
  createButton: {
    id: 'Reports.createForm.createButton',
    defaultMessage: 'Create Report',
    description: 'Create report button',
  },
});

const LayoutOption = styled.li`
  position: relative;
  background-color: white;
  border-radius: var(--cc_size_border_radius_l);
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  cursor: pointer;
`;
const LayoutOverlay = styled.div`
  display: flex;
  align-items: center;
`;
const LayoutTitle = styled.p`
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-weight: 500;
  color: var(--cc_color_text_default);
  margin-bottom: 0;
`;

const TitleDescWrapper = styled.div`
  margin-left: 20px;
`;

const Container = styled.div`
  max-width: 860px;
  display: grid;
  grid-gap: 16px;
  justify-content: center;
  grid-template-columns: 300px;
  grid-template-areas:
    'ill'
    'lyt';

  @media (min-width: 768px) {
    grid-template-areas: 'lyt ill';
    grid-template-columns: 1fr 300px;
    align-items: center;
    grid-gap: 32px;
  }
`;

const IllustrationContent = styled.div`
  grid-area: ill;
  position: relative;
  width: 300px;
  height: 260px;
  object-fit: contain;
  ${(props) => !props.isEmptyState && `padding: ${tokens.size_spacing_l}px 0`};
`;

const Illustration = styled.img`
  ${(props) => (props.status === 'error' ? 'filter: grayscale(1);' : null)}
  ${(props) => (props.status === 'error' ? 'opacity: 0.5' : null)}
  width: 100%;
  height: 100%;
  display: block;
`;

const CreateForm = ({
  values,
  handleSubmit,
  handleChange,
  setFieldValue,
  layouts,
}) => {
  const [selectedLayout, setSelectedLayout] = useState('');
  const [layoutImage, setLayoutImage] = useState(ReportPageLayouts);

  const handleLayoutSelection = (layout) => {
    setSelectedLayout(layout.id);
    setLayoutImage(LAYOUT_IMAGES[layout.name]);
    setFieldValue('layoutId', layout.id);
  };

  const handleSubmitForm = () => {
    handleSubmit();
  };

  const renderLayoutList = () => {
    return layouts.map((layout, idx) => {
      return (
        <LayoutOption
          key={layout.id}
          className={selectedLayout === layout.id ? 'selected' : null}
          data-testid={`reports__create-report-photo-layout-${idx}`}
        >
          <LayoutContainer
            className={selectedLayout === layout.id ? 'selected' : null}
            onClick={() => {
              handleLayoutSelection(layout);
            }}
          >
            <LayoutOverlay>
              <OptionWrapper>
                <SelectCircle selected={selectedLayout === layout.id} />
                <TitleDescWrapper>
                  <LayoutTitle>{layout.name}</LayoutTitle>
                </TitleDescWrapper>
              </OptionWrapper>
            </LayoutOverlay>
          </LayoutContainer>
        </LayoutOption>
      );
    });
  };

  return (
    <React.Fragment>
      <div>
        <div className="form-field">
          <FormRow className="input-wrap">
            <TextInput
              placeholder={messages.title.defaultMessage}
              name="title"
              value={values.title}
              onChange={handleChange}
              label={messages.title.defaultMessage}
              data-intercom-target="reports__create-report-title-input"
              data-testid="reports__create-report-title-input"
            />
            <ErrorMessage
              name="title"
              render={(msg) => <FormMessage>{msg}</FormMessage>}
            />
          </FormRow>
        </div>

        <div className="form-field">
          <FormRow>
            <FormLabel>Photo Layout (can change later)</FormLabel>
            <Container>
              <IllustrationContent isEmptyState={selectedLayout === ''}>
                <Illustration
                  src={layoutImage}
                  alt="Illustration showing report page layout"
                />
              </IllustrationContent>
              <LayoutList data-intercom-target="reports__create-report-layout-list">
                {renderLayoutList()}
                <ErrorMessage
                  name="layoutId"
                  render={(msg) => <FormMessage>{msg}</FormMessage>}
                />
              </LayoutList>
            </Container>
          </FormRow>
        </div>
      </div>
      <div className="btn-container">
        <Button
          color="primary"
          aria-label="Create Report"
          onClick={handleSubmitForm}
          data-intercom-target="reports__create-report-submit-button"
          data-testid="reports__create-report-submit-button"
        >
          {messages.createButton.defaultMessage}
        </Button>
      </div>
    </React.Fragment>
  );
};

CreateForm.propTypes = {
  values: PropTypes.object,
  handleSubmit: PropTypes.func,
  handleChange: PropTypes.func,
  setFieldValue: PropTypes.func,
  layouts: PropTypes.array,
};

export default CreateForm;
